// Potansiyel Cari Kartı — Cari kartının sade versiyonu (hareketler yok).
// "Cariye Aktar" ile gerçek cariye dönüşür; converted_to_account_id zinciri korunur.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import DataTable from '../components/DataTable';
import AttachmentManager from '../components/AttachmentManager';
import RecordChatter from '../components/RecordChatter';
import CustomFieldsSection from '../components/CustomFieldsSection';
import { useAuth } from '../context/AuthContext';
import { Button, EmptyState, Modal, FormField, Pager, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';
import { TabIcon } from '../utils/tabIcons';

const TYPES = [
  { value: 'customer',         label: 'Müşteri' },
  { value: 'supplier',         label: 'Tedarikçi' },
  { value: 'both',             label: 'Müşteri & Tedarikçi' },
  { value: 'retail_customer',  label: 'Perakende Müşteri' },
  { value: 'dealer',           label: 'Bayi' },
  { value: 'ecommerce',        label: 'E-Ticaret Müşteri' },
  { value: 'other',            label: 'Diğer' },
];

const STATUSES = [
  { value: 'open',       label: 'Açık' },
  { value: 'contacted',  label: 'İletişim Kuruldu' },
  { value: 'negotiation',label: 'Görüşmede' },
  { value: 'converted',  label: 'Cariye Aktarıldı' },
  { value: 'lost',       label: 'Kaybedildi' },
];

const STATUS_COLORS = {
  open:        { bg: 'rgba(100,116,139,0.15)', clr: '#64748b' },
  contacted:   { bg: 'rgba(59,130,246,0.15)',  clr: 'var(--info)' },
  negotiation: { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  converted:   { bg: 'rgba(16,185,129,0.15)',  clr: 'var(--success)' },
  lost:        { bg: 'rgba(239,68,68,0.15)',   clr: 'var(--danger)' },
};

export default function ProspectAccountsPage({ autoOpenNew = false, onSaved = null, onNavigate } = {}) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Varsayılan: aktarılmış olanlar da listede kalır (kullanıcı isterse "sadece aktif" seçebilir)
  const [filters, setFilters] = useState({ status: '', include_converted: 'yes' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/prospect-accounts', { params: { search, ...filters, page, limit } });
      if (r.data.success) { setList(r.data.data || []); setTotal(r.data.total || 0); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filters, page, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Overlay modunda açılır açılmaz "Yeni" formunu aç
  useEffect(() => {
    if (autoOpenNew) {
      setSelected(null); setIsNew(true); setIsEditing(true); setActiveTab('general'); setMsg(null);
      setForm({
        title: '', type: 'customer', identity_type: 'corporate',
        tax_office: '', tax_number: '', id_number: '',
        phone: '', mobile: '', email: '', website: '',
        address: '', district: '', city: '', country: 'Türkiye',
        contact_person: '', contact_title: '',
        sector: '', source: '', status: 'open',
        currency_code: 'TRY', notes: '',
      });
      setShowModal(true);
    }
  }, [autoOpenNew]);

  const handleNew = () => {
    setSelected(null); setIsNew(true); setIsEditing(true); setActiveTab('general'); setMsg(null);
    setForm({
      title: '', type: 'customer', identity_type: 'corporate',
      tax_office: '', tax_number: '', id_number: '',
      phone: '', mobile: '', email: '', website: '',
      address: '', district: '', city: '', country: 'Türkiye',
      contact_person: '', contact_title: '',
      sector: '', source: '', status: 'open',
      currency_code: 'TRY', notes: '',
    });
    setShowModal(true);
  };

  const handleSelect = async (row) => {
    try {
      const r = await api.get('/prospect-accounts/' + row.id);
      if (r.data.success) {
        setSelected(r.data.data); setIsNew(false); setIsEditing(false); setActiveTab('general'); setMsg(null);
        setForm({ ...r.data.data });
        setShowModal(true);
      }
    } catch (e) { alert('Kayıt yüklenemedi'); }
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.title?.trim()) { setMsg({ type: 'error', text: 'Ünvan zorunlu' }); return; }
    setSaving(true);
    try {
      let r;
      if (isNew) r = await api.post('/prospect-accounts', form);
      else       r = await api.put('/prospect-accounts/' + selected.id, form);
      if (r.data.success) {
        setMsg({ type: 'success', text: 'Kaydedildi' });
        setIsNew(false); setIsEditing(false);
        await fetchList();
        const newId = r.data.data?.id || selected?.id;
        if (newId) {
          const fresh = await api.get('/prospect-accounts/' + newId);
          if (fresh.data.success) {
            setSelected(fresh.data.data); setForm({ ...fresh.data.data });
            if (onSaved) onSaved(fresh.data.data);
          }
        }
      }
      if (mode === 'close') {
        setTimeout(() => setShowModal(false), 250);
      } else if (mode === 'new') {
        setTimeout(() => {
          setSelected(null); setForm({});
          setIsEditing(true); setIsNew(true); setMsg(null);
        }, 250);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Kayıt başarısız' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm(`"${selected.title}" silinsin mi?`)) return;
    try {
      await api.delete('/prospect-accounts/' + selected.id);
      setShowModal(false); await fetchList();
    } catch (e) { alert(e.response?.data?.message || 'Silme başarısız'); }
  };

  const handleConvert = async () => {
    if (!selected) return;
    if (selected.converted_to_account_id) {
      if (!window.confirm('Bu kayıt zaten cariye aktarılmış. Mevcut cariyi açmak ister misiniz?')) return;
      // Mevcut cari sayfasına yönlendir
      if (onNavigate) onNavigate('accounts');
      return;
    }
    if (!window.confirm(`"${selected.title}" gerçek cari kartına aktarılacak ve potansiyel listeden çıkacak. Devam edilsin mi?`)) return;
    setConverting(true);
    try {
      const r = await api.post('/prospect-accounts/' + selected.id + '/convert');
      if (r.data.success) {
        setMsg({ type: 'success', text: 'Cariye aktarıldı: ' + r.data.data.code + ' - ' + r.data.data.title });
        // Selected'i refresh et
        const fresh = await api.get('/prospect-accounts/' + selected.id);
        if (fresh.data.success) { setSelected(fresh.data.data); setForm({ ...fresh.data.data }); }
        await fetchList();
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Aktarım başarısız' }); }
    finally { setConverting(false); }
  };

  const tabs = [
    { key: 'general',   label: 'Genel',         icon: 'id' },
    { key: 'contact',   label: 'İletişim',      icon: 'phone' },
    { key: 'classify',  label: 'Sınıflandırma', icon: 'briefcase' },
    { key: 'custom',    label: 'Özel',          icon: 'sparkles' },
    { key: 'activity',  label: 'Aktivite',      icon: 'message' },
    { key: 'documents', label: 'Belgeler',      icon: 'paperclip' },
    { key: 'notes',     label: 'Notlar',        icon: 'notes' },
  ];

  const inEdit = isEditing;
  const isConverted = !!selected?.converted_to_account_id;
  const canEdit = !isConverted; // Aktarılmış kayıt salt-okunur

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Potansiyel Cariler"
        unit="potansiyel cari"
        count={total}
        searchValue={search}
        onSearch={setSearch}
        searchPlaceholder="Ünvan, kod, telefon, vergi no ile ara..."
        showFilterButton
        filtersActive={showFilterBar}
        onToggleFilters={() => setShowFilterBar(s => !s)}
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Potansiyel</Button>}
      />

      <FilterBar
        active={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'status', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, ...STATUSES.map(s => ({ value: s.value, label: s.label }))] },
          { key: 'include_converted', type: 'select', label: 'Aktarılanlar',
            options: [{ value: 'yes', label: 'Hepsi (aktarılanlar dahil)' }, { value: 'no', label: 'Sadece Aktif (aktarılmayanlar)' }] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="prospect-accounts-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Potansiyel cari yok"
          data={list}
          rowKey="id"
          onRowClick={row => handleSelect(row)}
          columns={[
            { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 110,
              cell: r => <span className="stock-code">{r.code || '-'}</span> },
            { id: 'title', header: 'Ünvan', accessor: 'title', sortable: true,
              cell: r => <span className="stock-name-cell">{r.title}</span> },
            { id: 'contact', header: 'İlgili Kişi', accessor: 'contact_person', sortable: true, width: 180,
              cell: r => <span className="stock-secondary">{r.contact_person || '-'}</span> },
            { id: 'phone', header: 'Telefon', accessor: r => r.phone || r.mobile || '', sortable: true, width: 140,
              cell: r => <span className="stock-secondary">{r.phone || r.mobile || '-'}</span> },
            { id: 'city', header: 'Şehir', accessor: 'city', sortable: true, width: 120,
              cell: r => <span className="stock-secondary">{r.city || '-'}</span> },
            { id: 'source', header: 'Kaynak', accessor: 'source', sortable: true, width: 140,
              cell: r => <span className="stock-secondary">{r.source || '-'}</span> },
            { id: 'status', header: 'Durum', accessor: 'status', sortable: true, width: 130,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => {
                const c = STATUS_COLORS[r.status] || STATUS_COLORS.open;
                const lbl = STATUSES.find(x => x.value === r.status)?.label || r.status;
                return <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: c.bg, color: c.clr }}>{lbl}</span>;
              } },
            { id: 'converted', header: 'Aktarıldı', accessor: r => r.converted_to_account_id ? 1 : 0, sortable: true, width: 130,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => r.converted_to_account_id
                ? <span title={r.converted_account_code + ' - ' + r.converted_account_title} style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: 'rgba(16,185,129,0.15)', color: 'var(--success)' }}>✓ {r.converted_account_code}</span>
                : <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>—</span> },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: r => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/prospect-accounts/${r.id}/audit`} /></span> },
          ]}
        />
      </div>

      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-secondary)' }}>
        <Pager page={page} total={total} limit={limit} onPage={setPage} onLimit={l => { setLimit(l); setPage(1); }} />
      </div>

      {/* MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Potansiyel Cari' : 'Potansiyel Cari Kartı'}
          size="xl"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {!isNew && !inEdit && !isConverted && (
                  <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {!isNew && !isConverted && (
                  <Button variant="success" onClick={handleConvert} loading={converting}>→ Cariye Aktar</Button>
                )}
                {inEdit && (
                  <Button variant="ghost" onClick={() => { if (isNew) setShowModal(false); else setIsEditing(false); }}>
                    {isNew ? 'Vazgeç' : 'İptal'}
                  </Button>
                )}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {!isNew && !inEdit && !isConverted && (
                  <Button onClick={() => setIsEditing(true)}>✎ Düzenle</Button>
                )}
                {!inEdit && (
                  <Button variant="ghost" onClick={() => setShowModal(false)}>Kapat</Button>
                )}
              </div>
            </>
          }
        >
          {/* Hero */}
          {!isNew && (
            <CardHero
              icon="🎯"
              code={selected?.code}
              title={selected?.title}
              subtitle={[selected?.contact_person, selected?.phone, selected?.tax_number].filter(Boolean).join(' · ')}
              statusBadge={
                isConverted
                  ? <CardStatusBadge label="✓ Cariye Aktarıldı" color="var(--success)" bg="rgba(22,163,74,0.15)" />
                  : <CardStatusBadge preset="pending" label="Potansiyel" />
              }
              kpis={[
                isConverted && selected?.converted_account_code && {
                  label: 'Cari Kodu', value: selected.converted_account_code, color: 'success',
                  sublabel: selected.converted_account_title?.substring(0, 25),
                },
                selected?.tax_office && { label: 'Vergi Dairesi', value: selected.tax_office.substring(0, 20), color: 'neutral' },
                selected?.city && { label: 'Şehir', value: selected.city, color: 'info' },
              ].filter(Boolean)}
            />
          )}

          {msg && <div style={{ padding: 10, borderRadius: 4, marginBottom: 12, background: msg.type === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(16,185,129,0.08)', color: msg.type === 'error' ? '#991b1b' : 'var(--success)' }}>{msg.text}</div>}

          <CardTabs
            items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon}/> }))}
            active={activeTab}
            onChange={setActiveTab}
          />

          <div className="detail-tabs" style={{ display: 'none' }}>
            {tabs.map(t => (
              <button key={t.key} className={'tab-btn' + (activeTab === t.key ? ' active' : '')} onClick={() => setActiveTab(t.key)}>
                <TabIcon name={t.icon} /><span>{t.label}</span>
              </button>
            ))}
          </div>

          {activeTab === 'general' && (
            <div className="form-grid">
              {isConverted && (
                <div className="form-row full" style={{
                  gridColumn: '1/-1',
                  padding: '10px 14px',
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <input type="checkbox" checked={true} disabled style={{ width: 18, height: 18, accentColor: 'var(--success)' }} />
                  <strong style={{ color: 'var(--success)', fontSize: 14 }}>Cariye Aktarıldı</strong>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    → <strong>{selected.converted_account_code}</strong> - {selected.converted_account_title}
                  </span>
                  {selected.converted_at && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12, marginLeft: 'auto' }}>
                      {new Date(selected.converted_at).toLocaleString('tr-TR')}
                    </span>
                  )}
                </div>
              )}
              <FormField label="Ünvan" required><input value={form.title || ''} onChange={e => f('title', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Kod"><input value={form.code || ''} disabled placeholder="Otomatik" /></FormField>
              <FormField label="Cari Tipi">
                <select value={form.type || 'customer'} onChange={e => f('type', e.target.value)} disabled={!inEdit || !canEdit}>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </FormField>
              <FormField label="Kimlik Tipi">
                <select value={form.identity_type || 'corporate'} onChange={e => f('identity_type', e.target.value)} disabled={!inEdit || !canEdit}>
                  <option value="corporate">Tüzel Kişi</option>
                  <option value="individual">Şahıs</option>
                </select>
              </FormField>
              {form.identity_type === 'individual' ? (
                <FormField label="TC Kimlik No"><input value={form.id_number || ''} onChange={e => f('id_number', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              ) : (
                <>
                  <FormField label="Vergi Dairesi"><input value={form.tax_office || ''} onChange={e => f('tax_office', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
                  <FormField label="Vergi No"><input value={form.tax_number || ''} onChange={e => f('tax_number', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
                </>
              )}
              <FormField label="Para Birimi">
                <select value={form.currency_code || 'TRY'} onChange={e => f('currency_code', e.target.value)} disabled={!inEdit || !canEdit}>
                  <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.status || 'open'} onChange={e => f('status', e.target.value)} disabled={!inEdit || !canEdit}>
                  {STATUSES.filter(s => s.value !== 'converted').map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="form-grid">
              <FormField label="İlgili Kişi"><input value={form.contact_person || ''} onChange={e => f('contact_person', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Pozisyon"><input value={form.contact_title || ''} onChange={e => f('contact_title', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Telefon"><input value={form.phone || ''} onChange={e => f('phone', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Mobil"><input value={form.mobile || ''} onChange={e => f('mobile', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="E-posta"><input type="email" value={form.email || ''} onChange={e => f('email', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Web Sitesi"><input value={form.website || ''} onChange={e => f('website', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <div className="form-row full"><FormField label="Adres" fullWidth><textarea rows={2} value={form.address || ''} onChange={e => f('address', e.target.value)} disabled={!inEdit || !canEdit} /></FormField></div>
              <FormField label="İlçe"><input value={form.district || ''} onChange={e => f('district', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Şehir"><input value={form.city || ''} onChange={e => f('city', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Ülke"><input value={form.country || 'Türkiye'} onChange={e => f('country', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Posta Kodu"><input value={form.postal_code || ''} onChange={e => f('postal_code', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
            </div>
          )}

          {activeTab === 'classify' && (
            <div className="form-grid">
              <FormField label="Sektör"><input value={form.sector || ''} onChange={e => f('sector', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Kaynak (Lead Source)" hint="Web, fuar, referans, soğuk arama vb."><input value={form.source || ''} onChange={e => f('source', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
              <FormField label="Özel Kod"><input value={form.special_code || ''} onChange={e => f('special_code', e.target.value)} disabled={!inEdit || !canEdit} /></FormField>
            </div>
          )}

          {activeTab === 'custom' && (
            selected?.id ? (
              <CustomFieldsSection
                entityType="prospect_account"
                entityId={selected.id}
                autoSave
                disabled={!inEdit || !canEdit}
              />
            ) : (
              <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                Özel alanları doldurmak için önce kaydı oluşturun.
              </div>
            )
          )}

          {activeTab === 'activity' && (
            selected?.id ? (
              <div style={{ minHeight: 360 }}>
                <RecordChatter
                  recordType="prospect_account"
                  recordId={selected.id}
                  currentUserId={user?.id}
                />
              </div>
            ) : (
              <div style={{ padding: 20, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                Aktivite akışı için önce kaydı oluşturun.
              </div>
            )
          )}

          {activeTab === 'documents' && (
            <AttachmentManager entityType="prospect_account" entityId={selected?.id || null} />
          )}

          {activeTab === 'notes' && (
            <FormField label="Notlar" fullWidth>
              <textarea rows={8} value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!inEdit || !canEdit} />
            </FormField>
          )}
        </Modal>
      )}
    </div>
  );
}
